import React, {useEffect, useState} from 'react';
import Input from '../Input';
import CustomSelect from '../CustomSelect';
import {toast} from 'react-toastify';
import report from '../../SVG/Report';
import {
  useCreateFuelReceiptReportMutation,
  useCreateReportMutation,
  useLazyEquipmentsByFacilityQuery,
} from '../../store/builder/inspections.api';
import {setLoader} from '../../store/builder/reducer';
import {useDispatch} from 'react-redux';
import {useErrorHandler} from '../../global/ErrorsHandler';
import MonthPicker from '../MonthPicker/MonthPicker';
import useLoader from '../../global/UseLoadingHandler';
import moment from 'moment';
import {useTranslation} from '../../hooks/useTranslation';

const thisMonth = {year: moment().year(), month: moment().month() + 1};

export default function ReportModal({title, exit, facility, type}) {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const [reportData, setReportData] = useState({
    facility_id: null,
    year: null,
    month_from: null,
    name: null,
  });
  const [months, setMonths] = useState([
    {value: 1, label: t('reportModal.months.january')},
    {value: 2, label: t('reportModal.months.february')},
    {value: 3, label: t('reportModal.months.march')},
    {value: 4, label: t('reportModal.months.april')},
    {value: 5, label: t('reportModal.months.may')},
    {value: 6, label: t('reportModal.months.june')},
    {value: 7, label: t('reportModal.months.july')},
    {value: 8, label: t('reportModal.months.august')},
    {value: 9, label: t('reportModal.months.september')},
    {value: 10, label: t('reportModal.months.october')},
    {value: 11, label: t('reportModal.months.november')},
    {value: 12, label: t('reportModal.months.december')},
  ]);
  const [years, setYears] = useState([]);
  const [monthError, setMonthError] = useState(false);
  const [error, setError] = useState(false);
  const [equipmentTypeOptions, setEquipmentTypeOptions] = useState([]);
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [disabledTo, setDisabledTo] = useState(null);
  const [showEquipmentType, setShowEquipmentType] = useState();
  const [fromYear, setFromYear] = useState(null);

  const [
    createReport,
    {
      data: createReportData,
      isError: createReportIsError,
      isLoading: createReportIsLoading,
      isSuccess: createReportIsSuccess,
      error: createReportError,
    },
  ] = useCreateReportMutation();

  const [
    createFuelReport,
    {
      data: createFuelReportData,
      isError: createFuelReportIsError,
      isLoading: createFuelReportIsLoading,
      isSuccess: createFuelReportIsSuccess,
      error: createFuelReportError,
    },
  ] = useCreateFuelReceiptReportMutation();

  const [
    getEquipments,
    {
      data: equipmentsData,
      isLoading: equipmentsLoading,
      error: equipmentsError,
      isSuccess: equipmentsIsSuccess,
    },
  ] = useLazyEquipmentsByFacilityQuery();

  const [errorHandler, setErrorHandler] = useErrorHandler([
    createReportError,
    createFuelReportError,
  ]);

  function generateArrayOfYears() {
    let max = new Date().getFullYear();
    let min = 2023;
    let years = [];

    for (let i = max; i >= min; i--) {
      years.push({label: i, value: i});
    }
    return years;
  }

  useLoader([equipmentsLoading]);

  useEffect(() => {
    if (reportData.facility_id && showEquipmentType) {
      getEquipments(reportData.facility_id);
    }
  }, [reportData.facility_id]);

  useEffect(() => {
    if (equipmentsData?.length > 0) {
      setEquipmentTypeOptions([
        ...(equipmentsData?.map(item => ({
          value: item?.id,
          label: item?.title,
        })) || []),
      ]);
    }

    if (reportData.equipment_type_id) {
      setEquipmentOptions([
        {value: null, label: t('reportModal.labels.all')},
        ...equipmentsData
          ?.filter(item => item.id === reportData.equipment_type_id)[0]
          ?.equipments.map(item => ({
            value: item.id,
            label: item.title,
            ...item,
          })),
      ]);
    }
  }, [equipmentsData, reportData.equipment_type_id]);

  useEffect(() => {
    if (createReportIsLoading || createFuelReportIsLoading) {
      dispatch(setLoader(true));
    }
    if (createReportIsSuccess) {
      dispatch(setLoader(false));
      toast.success(createReportData?.message);
      exit();
    }
    if (createFuelReportIsSuccess) {
      dispatch(setLoader(false));
      toast.success(createFuelReportData?.message);
      exit();
    }
  }, [
    createReportIsLoading,
    createReportIsSuccess,
    createFuelReportIsLoading,
    createFuelReportIsSuccess,
  ]);
  useEffect(() => {
    setYears(generateArrayOfYears);
  }, []);
  useEffect(() => {
    if (reportData?.month_from > reportData?.month_to) {
      if (reportData?.month_from && reportData?.month_to) {
        toast.error(t('reportModal.messages.monthValidation'));
      }
      setMonthError(true);
    } else {
      setMonthError(false);
    }
  }, [reportData.month_from, reportData.month_to]);

  const handleChange = e => {
    if (
      e.target.name === 'equipment_type_id' ||
      e.target.name === 'facility_id'
    ) {
      setReportData(old => ({...old, month_from: null, month_to: null}));
      setFromYear(null);
    }
    if (e.target.name === 'facility_id') {
      const defaultType = facility.find(
        item => item.value === e.target.value,
      )?.default_type;
      if (defaultType === 'fueling') {
        setShowEquipmentType(true);
      } else {
        setShowEquipmentType(false);
      }
      e.target.value || reportData.facility_id
        ? setReportData(old => ({
            ...old,
            equipment_type_id: null,
            equipment_id: null,
          }))
        : null;
    }

    if (e.target.name === 'equipment_type_id') {
      setReportData(old => ({...old, equipment_id: null}));
    }

    setReportData(old => ({...old, [e.target.name]: e.target.value}));
  };
  const createReportFunc = () => {
    if (
      reportData?.name &&
      reportData?.month_from &&
      reportData?.year &&
      reportData?.facility_id
    ) {
      if (type === 'report') {
        createReport(reportData);
      } else {
        createFuelReport(reportData);
      }
    } else {
      toast.error(t('reportModal.messages.fillAllRequiredFields'));
    }
  };

  return (
    <div className='modal'>
      <div className='modal__content'>
        <div className='modal__content-head'>
          <p>{title}</p>
          <img
            className='modal__content-exit'
            src='/assets/icons/close.svg'
            alt='close'
            onClick={exit}
          />
        </div>
        <div className='modal__content-body report'>
          <Input
            id='name'
            name='name'
            placeholder={t('reportModal.form.reportName')}
            label={t('reportModal.form.reportName')}
            type={'text'}
            value={reportData.name}
            onChange={handleChange}
          />
          <CustomSelect
            name={'facility_id'}
            label={t('reportModal.labels.facilityName')}
            options={facility}
            value={facility.find(item => item.value === reportData.facility_id)}
            onChange={handleChange}
          />
          {showEquipmentType && (
            <>
              <CustomSelect
                defaultValue={
                  reportData?.equipment_type_id
                    ? equipmentTypeOptions.filter(
                        item => item.value === reportData.equipment_type_id,
                      )[0]
                    : ''
                }
                name={'equipment_type_id'}
                label={t('reportModal.labels.equipmentType')}
                options={equipmentTypeOptions}
                onChange={e => {
                  handleChange(e);
                  setReportData(old => ({...old, equipment_id: null}));
                  setReportData(old => ({...old, month_from: null}));
                  setReportData(old => ({...old, month_to: null}));
                }}
              />
              {reportData.equipment_type_id && (
                <CustomSelect
                  defaultValue={
                    reportData.equipment_id
                      ? equipmentOptions.filter(
                          item => item.value === reportData.equipment_id,
                        )[0]
                      : ''
                  }
                  name={'equipment_id'}
                  label={t('reportModal.labels.equipmentId')}
                  options={equipmentOptions}
                  onChange={e => {
                    handleChange(e);
                    if (e.target.value === null) {
                      setDisabledTo(null);
                    } else {
                      const currentEquipmentCreatedDate = equipmentOptions.find(
                        item => item.value === e.target.value,
                      )?.created_at;
                      setDisabledTo({
                        year: moment(currentEquipmentCreatedDate).year(),
                        month: moment(currentEquipmentCreatedDate).month() + 1,
                      });
                    }
                  }}
                />
              )}
            </>
          )}
          {reportData.facility_id && !reportData.equipment_type_id ? (
            <MonthPicker
              mode='single'
              label={t('reportModal.labels.month')}
              name='month_from'
              onChange={data => setReportData(old => ({...old, ...data}))}
              disabledFrom={disabledTo}
              disabledTo={thisMonth}
            />
          ) : (
            <>
              <MonthPicker
                mode='single'
                label={t('reportModal.labels.fromMonth')}
                name='month_from'
                onChange={data => {
                  setFromYear(data.year);
                  setReportData(old => ({
                    ...old,
                    month_from: data.month_from,
                    month_to: null,
                    year: data.year,
                  }));
                }}
                disabledFrom={disabledTo}
                disabledTo={thisMonth}
                clearable
              />
              <MonthPicker
                key={fromYear}
                mode='single'
                label={t('reportModal.labels.toMonth')}
                name='month_to'
                initialYear={fromYear}
                disabled={!reportData.month_from}
                disabledFrom={
                  fromYear
                    ? (disabledTo && disabledTo.year === fromYear ? disabledTo : {year: fromYear, month: 1})
                    : disabledTo
                }
                disabledTo={
                  fromYear
                    ? (fromYear < thisMonth.year ? {year: fromYear, month: 12} : thisMonth)
                    : thisMonth
                }
                clearable
                onChange={data => {
                  setReportData(old => ({
                    ...old,
                    month_to: data.month_from,
                    year: data.year,
                  }));
                }}
              />
            </>
          )}
        </div>
        <div className='modal__content-bottom'>
          <button className='close' onClick={exit}>
            {t('reportModal.buttons.close')}
          </button>
          <button className='save' onClick={createReportFunc}>
            {t('reportModal.buttons.create')}
          </button>
        </div>
      </div>
      <div className='modal__bg'></div>
    </div>
  );
}
